import type { EntityDetailSchema } from '@web-config-app/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useEntities } from '../use-entities/use-entities';
import type { LoadEntityRelationshipsSchema } from '../../types/operation.types';

interface UseEntityRelationshipsSchemasProps {
  schemaPath?: string;
}
/**
 * Loads the schemas for the passed entity types. Since these
 * need to be loaded asynchronously, via fetch, it is implemented
 * using the query client directly.
 * @param entityTypes - The entity types to load the schemas for.
 * @param schemaPath - The path to the schemas. Defaults to `/schemas`.
 * @returns A function that loads the schemas for the passed entity types.
 * The function returns an array of objects, each containing the schema,
 * the entity ID, and the status of the load operation.
 * The status can be either 'success' or 'error'.
 */

export const useLoadEntityRelationshipsSchemas = ({
  schemaPath = '/schemas',
}: UseEntityRelationshipsSchemasProps = {}): LoadEntityRelationshipsSchema => {
  const queryClient = useQueryClient();
  const entities = useEntities();

  return useCallback(
    async (entityTypes: string[]) => {
      const result = await Promise.all(
        entityTypes.map(async (entityType) => {
          try {
            const entity = entities.get(entityType);
            const schema = await queryClient.fetchQuery<EntityDetailSchema>({
              queryKey: ['schemas', entity?.id],
              queryFn: async () => {
                const response = await fetch(
                  `${schemaPath}/${entity?.id}.json`,
                );
                return response.json();
              },
              staleTime: Infinity,
              retry: false,
            });

            return {
              schema,
              id: entity?.id,
              type: entityType,
              status: 'success',
            };
          } catch (error) {
            return { type: entityType, error, status: 'error' };
          }
        }),
      );

      return result;
    },
    [queryClient, schemaPath, entities],
  );
};
